import React, { useCallback, useEffect, useState } from 'react';
import Panel from './Panel';
import Spacer from './Spacer';
import Separator from './Separator';
import SearchField from './SearchField';
import { CustomEventTypes, LayoutOrientations } from '../../globals';
import Input from '../../classes/Input'
import TextUtils from '../../classes/utilities/TextUtils';
import { IDropdownOption } from '../../interfaces/Misc';
import { IconFillStyles, TriangleIconRight } from '../Iconset';
import useEventListener from '../reactUtils/useEventListener';
import { KeyCodes } from '../../interfaces/Input';

interface IDropDownListProps
{
	options: IDropdownOption[],
	selected: any | any[],
	onSelectionChanged: (value: any) => void,
	searchable?: boolean,
	multiSelect?: boolean,
	decorators?: React.ReactNode,
	inverse?: boolean,
	maxHeight?: number,
}

/**
 * the display portion of the dropdown list, showing the list of options in a scrollable container
 *
 * this component is designed for use only as part of the CustomDropdown component
 * @param {*} props
 * @returns
 */
export function DropDownList(props: IDropDownListProps)
{
	const [searchQuery, setSearchQuery] = useState("");
	const [currentIndex, setCurrentIndex] = useState(-1);
	const [filteredOptions, setFilteredOptions] = useState<IDropdownOption[]>([]);

	useEffect(() =>
	{
		const newFilteredOptions = new Array<IDropdownOption>();
		for(const option of props.options)
		{
			const optionText = option.tag !== undefined ? option.tag : typeof (option.display) === "string" ? option.display : "";
			if (TextUtils.searchString(optionText, searchQuery))
			{
				newFilteredOptions.push(option);
			}
		}
		setFilteredOptions(newFilteredOptions);
	}, [props.options, searchQuery]);

	const handleInputUpdated = useCallback(() =>
	{
		if (Input.getKeyDown(KeyCodes.DOWN_ARROW))
		{
			if (currentIndex < props.options.length - 1)
			{
				setCurrentIndex(currentIndex + 1);
			}
		}
		else if (Input.getKeyDown(KeyCodes.UP_ARROW))
		{
			if (currentIndex > 0)
			{
				setCurrentIndex(currentIndex - 1);
			}
		}
		else if (Input.getKeyDown(KeyCodes.ENTER))
		{
			if (currentIndex >= 0 && currentIndex <= props.options.length - 1)
			{
				if (props.onSelectionChanged)
				{
					props.onSelectionChanged(props.options[currentIndex].value);
				}
			}
		}
	}, [props.options, currentIndex, props.onSelectionChanged]);
	useEventListener(!props.multiSelect ? CustomEventTypes.onInputUpdated : "", handleInputUpdated, document);

	return (
		<Panel className={["dark dropDownListContainer", (props.inverse ? "inverse" : "")].join(' ')}
			data-name={props["data-name"]}
			style={{
				maxHeight: props.maxHeight + "px",
			}}
		>
			{
				props.searchable &&
					<>
						<SearchField value={searchQuery} onChangeCallback={setSearchQuery} autoFocus />
						<Spacer size={{ y: "2px" }} />
					</>
			}
			<DropDownListOptions 
				options={filteredOptions}
				multiSelect={props.multiSelect}
				onSelectionChanged={props.onSelectionChanged}
				selected={props.selected}
				currentIndex={currentIndex}
			/>
			{
				!props.decorators ? null :
					<>
						<Spacer size={{ y: "4px" }} />
						<Separator className={"solid"} orientation={LayoutOrientations.Horizontal} />
						<Spacer size={{ y: "4px" }} />
						{props.decorators}
					</>
			}
		</Panel>
	);
}

interface IDropDownListOptionsProps
{
	options: IDropdownOption[],
	multiSelect?: boolean,
	selected: any | any[],
	currentIndex: number,
	onSelectionChanged: (value: any) => void,
}

/**
 * the display portion of the dropdown list, showing the list of options in a scrollable container
 *
 * this component is designed for use only as part of the DropDownList component
 * @param {IDropDownListOptionsProps} props
 * @returns
 */
function DropDownListOptions(props: IDropDownListOptionsProps)
{
	const [selectedOptions, setSelectedOptions] = useState<Set<any>>(new Set());
	
	useEffect(() =>
	{
		if (props.selected !== undefined)
		{
			if (Array.isArray(props.selected))
			{
				setSelectedOptions(new Set(props.selected));
			}
			else
			{
				setSelectedOptions(new Set([props.selected]));
			}
		}
	}, [props.selected]);

	return props.options?.map((option, index) => 
	{
		return (
			<DropDownListItem
				key={index}
				option={option}
				selected={selectedOptions.has(option.value)}
				candidate={!selectedOptions.has(option.value) && index === props.currentIndex}
				disabled={option.disabled}
				multiSelect={props.multiSelect}
				tooltip={option.tooltip}
				onItemToggled={(value, selected) =>
				{	
					const newSelection = new Set(props.multiSelect ? selectedOptions : []); // if multiSelect is false, we'll replace the selection with the new value
					if (selected)
					{
						newSelection.add(value);
					}
					else
					{
						newSelection.delete(value);
					}

					setSelectedOptions(newSelection);
					props.onSelectionChanged?.(props.multiSelect ? Array.from(newSelection) : value);
				}}				
			/>
		);
	});
}

interface IDropDownListItemProps
{
	option: IDropdownOption,
	selected: boolean,
	candidate: boolean,
	disabled?: boolean,
	multiSelect?: boolean,
	onItemToggled: (value: string, selected: boolean) => void,
	tooltip?: string,
}

/**
 * the display portion of each option entry in the dropdown list
 *
 * this component is designed for use only as part of the DropDownList component
 * @param {*} props
 * @returns
 */
function DropDownListItem(props: IDropDownListItemProps)
{
	return (
		<div className={"dropDownListItemRoot"}
			data-name={"dropdown-option-" + props.option.value}
			data-disabled={props.disabled ? "true" : "false"}
			data-status={props.selected ? "selected" :
				props.candidate ? "candidate" :
					"none"
			}
			onClick={(e) =>
			{
				if(props.disabled) return;
				if (props.onItemToggled)
				{
					props.onItemToggled(props.option.value, props.multiSelect ? !props.selected : true);
				}
			}}
			data-tooltip={props.tooltip}
		>
			{
				!props.multiSelect ? null :
					<div className={"indent"}>
						{
							!props.selected ? null :
								<TriangleIconRight scale={0.7} fillStyle={props.selected ? IconFillStyles.dark : IconFillStyles.default } />

						}
					</div>
			}
			<div className={"dropDownListItemTitle"}>
				<Panel orientation={LayoutOrientations.Horizontal} style={{ overflow: 'hidden', alignItems: 'center' }}>
					{props.option.display}
				</Panel>
			</div>
		</div>
	);
}